import { ApiError } from '../errors';
import type { HttpClient, RequestOptions } from '../http';
import type {
  BBox, CancelTripResponse, ConversationCurrent, FloodView, Me, QuoteConfirmResponse,
  SendMessageRequest, SendMessageResponse, SosRequest, SosResponse, TripView, UUID,
  BookingPlace, BookingSuggestion, ManualQuote, ManualQuoteRequest, Point, Place,
} from './types';

/** Đường dẫn v3, tương đối với gốc `/api/v3` (hợp đồng 10 §3). */
const enc = encodeURIComponent;
export const v3Paths = {
  me: '/me',
  conversationCurrent: '/conversations/current',
  conversationMessages: '/conversations/messages',
  customerCurrentTrip: '/customer/trips/current',
  trip: (id: UUID) => `/trips/${enc(id)}`,
  tripCancel: (id: UUID) => `/trips/${enc(id)}/cancel`,
  quoteConfirm: (id: UUID) => `/quotes/${enc(id)}/confirm`,
  floods: '/floods',
  sos: '/sos',
  stream: '/stream',
} as const;

export interface V3ApiOptions {
  /** Ghi lỗi kèm X-Request-Id (00 §7, 05 §2). Mặc định console.warn. */
  logError?: (message: string, error: ApiError) => void;
}

/**
 * Client API v3 cho app khách (FE-1.1). HttpClient phải tạo với `responseEnvelope: 'v3'` và baseUrl `/api/v3`.
 * Mọi lệnh ghi bắt buộc nhận idempotencyKey; gọi lại cùng key ⇒ Backend trả lại kết quả cũ.
 */
export function createV3Api(http: HttpClient, opts: V3ApiOptions = {}) {
  const logError = opts.logError ?? ((msg, e) => console.warn(msg, { code: e.code, status: e.status, requestId: e.requestId }));
  let clockOffsetMs = 0;

  async function call<T>(path: string, o: RequestOptions = {}): Promise<T> {
    const startedAt = Date.now();
    try {
      const response = await http.request<T>(path, o);
      // HTTP Date uses the same clock as observed_at/ETA. Device clocks can be minutes off.
      const serverTime = Date.parse(response.headers.get('Date') ?? '');
      if (Number.isFinite(serverTime)) clockOffsetMs = serverTime - (startedAt + Date.now()) / 2;
      return response.data;
    } catch (e) {
      if (e instanceof ApiError) logError(`[api v3] ${o.method ?? 'GET'} ${path} lỗi ${e.code}`, e);
      throw e;
    }
  }
  const write = <T>(path: string, body: unknown, idempotencyKey: string) => {
    if (!idempotencyKey) throw new Error('Lệnh ghi v3 bắt buộc có Idempotency-Key');
    return call<T>(path, { method: 'POST', body, idempotencyKey });
  };

  return {
    serverNow: (localNow = Date.now()) => localNow + clockOffsetMs,
    me: (signal?: AbortSignal) => call<Me>(v3Paths.me, { signal }),

    conversations: {
      clear: () => call<ConversationCurrent>(v3Paths.conversationCurrent, { method: 'DELETE' }),
      current: (p: { limit?: number; after?: UUID } = {}, signal?: AbortSignal) =>
        call<ConversationCurrent>(v3Paths.conversationCurrent, { query: { limit: p.limit, after: p.after }, signal }),
      /** Trả 202 ngay; câu trả lời của trợ lý đến qua SSE `message.created`. Key = client_message_id. */
      send: (body: SendMessageRequest) =>
        write<SendMessageResponse>(v3Paths.conversationMessages, body, body.client_message_id),
    },

    customer: {
      notifications: (p: { cursor?: string; category?: string } = {}, signal?: AbortSignal) =>
        call<import('../types').Page<import('../types').NotificationItem>>('/customer/notifications', { query: p, signal }),
      markNotificationSeen: (id: string) =>
        call<{ seen: boolean }>(`/customer/notifications/${enc(id)}/seen`, { method: 'POST' }),
      history: (p: { cursor?: string; state?: string; limit?: number } = {}, signal?: AbortSignal) =>
        call<{ trips: TripView[]; next_cursor: string | null }>('/customer/trips', { query: p, signal }),
      /** Chuyến chưa kết thúc gần nhất, hoặc null. */
      currentTrip: async (signal?: AbortSignal) =>
        (await call<{ trip: TripView | null }>(v3Paths.customerCurrentTrip, { signal })).trip,
    },

    booking: {
      suggestions: (q: string, signal?: AbortSignal) =>
        call<{ suggestions: BookingSuggestion[] }>('/customer/booking/suggestions', { query: { q }, signal }),
      resolveSuggestion: (refId: string, signal?: AbortSignal) =>
        call<Place>('/customer/booking/suggestions/resolve', { query: { ref_id: refId }, signal }),
      places: (q: string, near?: Point, signal?: AbortSignal) =>
        call<{ places: BookingPlace[]; ambiguous: boolean }>('/customer/booking/places', {
          query: { q, near_lat: near?.lat, near_lon: near?.lon }, signal,
        }),
      quote: (body: ManualQuoteRequest, idempotencyKey: string) =>
        write<ManualQuote>('/customer/booking/quotes', body, idempotencyKey),
    },

    trips: {
      get: (tripId: UUID, signal?: AbortSignal) => call<TripView>(v3Paths.trip(tripId), { signal }),
      cancel: (tripId: UUID, reason: string | null, idempotencyKey: string) =>
        write<CancelTripResponse>(v3Paths.tripCancel(tripId), { reason }, idempotencyKey),
    },

    quotes: {
      confirm: (quoteId: UUID, confirm: boolean, idempotencyKey: string) =>
        write<QuoteConfirmResponse>(v3Paths.quoteConfirm(quoteId), { confirm }, idempotencyKey),
    },

    floods: {
      /** Khách chỉ nhận điểm VERIFIED; Backend bỏ qua tham số status. */
      list: (bbox: BBox, signal?: AbortSignal) =>
        call<FloodView[]>(v3Paths.floods, { query: { bbox: bbox.join(',') }, signal }),
    },

    sos: {
      create: (body: SosRequest, idempotencyKey: string) => write<SosResponse>(v3Paths.sos, body, idempotencyKey),
    },
  };
}

export type V3Api = ReturnType<typeof createV3Api>;
